/**
 * Cómo leer una negativa del API sin enseñar nunca su texto.
 *
 * Las pantallas deciden por el código HTTP primero y por `detail.kind` después
 * (contrato de `docs/handoff/NOTAS-acceso.md`), y siempre pintan una frase de
 * i18n: el `detail` o el `err.message` crudos pueden decir si una cuenta existe,
 * o salir en inglés de una librería.
 */

interface RespuestaDeError {
  status?: number;
  data?: { detail?: unknown };
  headers?: Record<string, string | undefined>;
}

function respuesta(err: unknown): RespuestaDeError | null {
  const r = (err as { response?: RespuestaDeError } | null)?.response;
  return r && typeof r === 'object' ? r : null;
}

/** Código HTTP de la negativa; 0 si no hubo respuesta (red caída, CORS, timeout). */
export function estadoHttp(err: unknown): number {
  const status = respuesta(err)?.status;
  return typeof status === 'number' ? status : 0;
}

/** `detail.kind` (o `detail` si el API lo manda como cadena); null si no hay. */
export function tipoDeNegativa(err: unknown): string | null {
  const detail = respuesta(err)?.data?.detail;
  if (typeof detail === 'string') return detail;
  const kind = (detail as { kind?: unknown } | null | undefined)?.kind;
  return typeof kind === 'string' ? kind : null;
}

/**
 * Segundos de `Retry-After`; null si no llega o no es un número.
 *
 * Para que el navegador deje leerla en una petición entre orígenes, el API tiene
 * que exponerla (`Access-Control-Expose-Headers: Retry-After`); sin eso llega
 * null y la pantalla dice «espera unos minutos» sin cifra.
 */
export function segundosDeEspera(err: unknown): number | null {
  const valor = respuesta(err)?.headers?.['retry-after'];
  const segundos = valor === undefined ? NaN : Number.parseInt(String(valor), 10);
  return Number.isFinite(segundos) && segundos > 0 ? segundos : null;
}
